import React, { useState, useRef, useEffect, useCallback } from "react";
import countryList from "./countryList";

const Currency: React.FC = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [amount, setAmount] = useState("1.00");
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("CNY");
  const [isAmountFocused, setIsAmountFocused] = useState(false);

  const getSymbol = (currency: string) =>
    countryList[currency as keyof typeof countryList]?.currencysymbol ||
    currency;
  const [fromSearch, setFromSearch] = useState("");
  const [toSearch, setToSearch] = useState("");
  const [isFromOpen, setIsFromOpen] = useState(false);
  const [isToOpen, setIsToOpen] = useState(false);
  const [rates, setRates] = useState<{ [key: string]: number }>({});
  const [conversionResult, setConversionResult] = useState<{
    convertedAmount: number;
    rate: number;
    lastUpdated: string;
  } | null>(null);
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fromRef.current && !fromRef.current.contains(event.target as Node)) {
        setIsFromOpen(false);
      }
      if (toRef.current && !toRef.current.contains(event.target as Node)) {
        setIsToOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredFromCountries = Object.entries(countryList).filter(
    ([code, { name }]) =>
      code.toLowerCase().includes(fromSearch.toLowerCase()) ||
      name.toLowerCase().includes(fromSearch.toLowerCase())
  );

  const filteredToCountries = Object.entries(countryList).filter(
    ([code, { name }]) =>
      code.toLowerCase().includes(toSearch.toLowerCase()) ||
      name.toLowerCase().includes(toSearch.toLowerCase())
  );

  const [error, setError] = useState<string | null>(null);

  const fetchRates = useCallback(async (retryCount = 0) => {
    try {
      const response = await fetch(
        "https://api.exchangerate-api.com/v4/latest/USD"
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (!data || !data.rates) {
        throw new Error(
          "The format of the data returned by the API is incorrect!"
        );
      }
      setRates(data.rates);
      setError(null);
    } catch (error) {
      console.error("Exchange rate update failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (retryCount < 2) {
        console.log(`Retry ${retryCount + 1} times...`);
        setTimeout(() => fetchRates(retryCount + 1), 1000 * (retryCount + 1));
      } else {
        let userError = "Unable to obtain exchange rate data!";
        if (errorMessage.includes("Failed to fetch")) {
          userError =
            "The network connection failed. Please check your network connection.";
        } else if (errorMessage.includes("HTTP error")) {
          userError = `Server response error (${errorMessage})`;
        } else if (
          errorMessage.includes(
            "The format of the data returned by the API is incorrect!"
          )
        ) {
          userError =
            "The API data format is incorrect, please try again later.";
        }
        setError(userError);
      }
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const calculateConversion = useCallback(() => {
    setError(null);
    try {
      if (!rates[fromCurrency] || !rates[toCurrency]) {
        throw new Error("Unable to obtain exchange rate data!");
      }

      const usdAmount = parseFloat(amount) / rates[fromCurrency];
      const convertedAmount = usdAmount * rates[toCurrency];
      const rate = rates[toCurrency] / rates[fromCurrency];

      setConversionResult({
        convertedAmount,
        rate,
        lastUpdated: new Date().toLocaleString("en-US"),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred.");
      setConversionResult(null);
    }
  }, [amount, fromCurrency, toCurrency, rates]);

  useEffect(() => {
    if (amount && fromCurrency && toCurrency && Object.keys(rates).length > 0) {
      calculateConversion();
    }
  }, [amount, fromCurrency, toCurrency, rates, calculateConversion]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="w-full flex justify-center items-center mt-5">
      <div
        className="w-[90%] max-w-[1116px] bg-white py-5 px-10 rounded-3xl"
        style={{ boxShadow: "1px 3px 10px rgba(0, 0, 0, 0.2)" }}
      >
        <div className="max-w-[800px] mx-auto flex flex-col gap-5">
          <h1
            className={`${
              windowWidth > 495 ? "text-4xl" : "text-3xl"
            } font-semibold text-center text-blue-900`}
          >
            Currency Exchange
          </h1>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col justify-between md:flex-row gap-4 items-start">
                <div className="w-full md:w-[40%] space-y-2">
                  <label className="text-base font-semibold text-gray-800 block">
                    From
                  </label>
                  <div className="relative" ref={fromRef}>
                    <div
                      className="relative w-full pl-3 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex items-center"
                      onClick={() => setIsFromOpen(!isFromOpen)}
                    >
                      <div className="flex items-center min-w-0 w-[300px]">
                        <span className="flex-shrink-0 mr-2">
                          {
                            countryList[
                              fromCurrency as keyof typeof countryList
                            ].flag
                          }
                        </span>
                        <span className="flex-shrink-0 mr-2">
                          {fromCurrency}
                        </span>
                        <span className="truncate">
                          -{" "}
                          {
                            countryList[
                              fromCurrency as keyof typeof countryList
                            ].name
                          }
                        </span>
                      </div>
                      <svg
                        className={`absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 transition-transform ${
                          isFromOpen ? "transform rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    {isFromOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={fromSearch}
                          onChange={(e) => setFromSearch(e.target.value)}
                          className="w-full p-2 border-b focus:outline-none"
                        />
                        {filteredFromCountries.map(([code, { name, flag }]) => (
                          <div
                            key={code}
                            className={`p-2 cursor-pointer hover:bg-gray-100 ${
                              fromCurrency === code ? "bg-gray-50" : ""
                            } flex items-center`}
                            onClick={() => {
                              setFromCurrency(code);
                              setIsFromOpen(false);
                              setFromSearch("");
                            }}
                          >
                            <span className="flex-shrink-0 mr-2">{flag}</span>
                            <span className="flex-shrink-0 mr-2">{code}</span>
                            <span className="truncate flex-1">- {name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={`flex items-center justify-center w-full md:w-[20%] ${
                    windowWidth > 767 ? "mt-6" : "mt-0"
                  }`}
                >
                  <button
                    onClick={() => {
                      setFromCurrency(toCurrency);
                      setToCurrency(fromCurrency);
                    }}
                    className="p-2.5 rounded-full hover:bg-gray-100 transition-colors duration-200 border-2 border-gray-200 focus:outline-none focus:ring-0 focus:ring-blue-0"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-600"
                    >
                      <path d="M17 8l4 4-4 4M3 12h18M7 16l-4-4 4-4" />
                    </svg>
                  </button>
                </div>

                <div className="w-full md:w-[40%] space-y-2">
                  <label className="text-base font-semibold text-gray-800 block">
                    To
                  </label>
                  <div className="relative" ref={toRef}>
                    <div
                      className="relative w-full pl-3 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex items-center"
                      onClick={() => setIsToOpen(!isToOpen)}
                    >
                      <div className="flex items-center min-w-0 w-[300px]">
                        <span className="flex-shrink-0 mr-2">
                          {
                            countryList[toCurrency as keyof typeof countryList]
                              .flag
                          }
                        </span>
                        <span className="flex-shrink-0 mr-2">{toCurrency}</span>
                        <span className="truncate">
                          -{" "}
                          {
                            countryList[toCurrency as keyof typeof countryList]
                              .name
                          }
                        </span>
                      </div>
                      <svg
                        className={`absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 transition-transform ${
                          isToOpen ? "transform rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    {isToOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={toSearch}
                          onChange={(e) => setToSearch(e.target.value)}
                          className="w-full p-2 border-b focus:outline-none"
                        />
                        {filteredToCountries.map(([code, { name, flag }]) => (
                          <div
                            key={code}
                            className={`p-2 cursor-pointer hover:bg-gray-100 ${
                              toCurrency === code ? "bg-gray-50" : ""
                            } flex items-center`}
                            onClick={() => {
                              setToCurrency(code);
                              setIsToOpen(false);
                              setToSearch("");
                            }}
                          >
                            <span className="flex-shrink-0 mr-2">{flag}</span>
                            <span className="flex-shrink-0 mr-2">{code}</span>
                            <span className="truncate flex-1">- {name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col justify-between md:flex-row gap-4 items-start">
                  <div className="w-full md:w-[40%] space-y-2">
                    <label className="text-base font-semibold text-gray-800 block">
                      Amount
                    </label>
                    <div
                      className={`flex items-center p-2 border-2 rounded-xl transition-all ${
                        isAmountFocused
                          ? "ring-1 ring-blue-500 border-blue-500"
                          : ""
                      }`}
                    >
                      <span className="text-gray-800 text-lg">
                        {getSymbol(fromCurrency)}
                      </span>
                      <input
                        type="text"
                        value={amount}
                        onChange={(e) => {
                          const value = e.target.value.trim();
                          if (!isNaN(Number(value))) {
                            setAmount(value);
                          }
                        }}
                        onFocus={() => setIsAmountFocused(true)}
                        onBlur={() => setIsAmountFocused(false)}
                        placeholder="0.00"
                        className="w-full pl-3 text-gray-800 text-xl font-semibold focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="w-full md:w-[40%] space-y-2">
                    <label className="text-base font-semibold text-gray-800 block">
                      Result
                    </label>
                    <div className="p-2 bg-gray-50 border-2 rounded-xl">
                      {/* <span className="text-xl font-semibold text-gray-800 whitespace-nowrap overflow-hidden text-overflow-ellipsis">
                        {conversionResult
                          ? `${conversionResult.convertedAmount.toFixed(
                              8
                            )} ${toCurrency}`
                          : "--"}
                      </span> */}
                      <div className="flex items-center">
                        <span className="text-gray-800 text-lg">
                          {getSymbol(toCurrency)}
                        </span>
                        <span className="pl-3 text-gray-800 text-xl font-semibold overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                          {conversionResult
                            ? conversionResult.convertedAmount.toFixed(8)
                            : "--"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-lg">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Currency;
